import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@examdojo/core/button';
import { POPOVER_ID_DATA_ATTR } from '@examdojo/tiptap-popover';
import { isExternalUrl } from '@examdojo/core/util/url-utils';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  tap,
} from 'rxjs';
import { ContentBlock, MarkdownBlock } from '../../../model';

export const POPOVER_TRIGGER_CLASS = 'popover-trigger';

const HIDE_DELAY_MS = 100;

@Component({
  selector: 'dojo-popover-trigger-host',
  templateUrl: './popover-trigger-host.component.html',
  styleUrl: './popover-trigger-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatMenuModule, ButtonComponent, RouterLink, TranslocoPipe, NgComponentOutlet, NgTemplateOutlet],
})
export class PopoverTriggerHostComponent {
  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    merge(this.mouseEnterTrigger$, this.mouseLeaveTrigger$, this.closePopoverAfterDelay())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly block = input.required<MarkdownBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();

  private readonly popovers = computed(() => this.block().popovers ?? {});
  private readonly menuTrigger = viewChild.required<MatMenuTrigger>('trigger');

  readonly popoverContent = signal<ContentBlock[]>([]);
  readonly popoverLinkUrl = signal<string | undefined>(undefined);
  readonly isExternalLink = computed(() => {
    const url = this.popoverLinkUrl();
    return !!url && isExternalUrl(url);
  });
  readonly anchorPosition = signal({ x: 0, y: 0, width: 0, height: 0 });

  private currentTriggerElement: HTMLElement | null = null;

  private readonly isMouseOverTrigger$$ = new BehaviorSubject(false);
  private readonly isMouseOverPopover$$ = new BehaviorSubject(false);

  private readonly shouldBeVisible$ = combineLatest([this.isMouseOverTrigger$$, this.isMouseOverPopover$$]).pipe(
    map(([overTrigger, overPopover]) => overTrigger || overPopover),
    distinctUntilChanged(),
  );

  private readonly mouseEnterTrigger$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mouseover').pipe(
    tap((event) => {
      const target = event.target as HTMLElement;
      const triggerEl = target.closest(`.${POPOVER_TRIGGER_CLASS}`) as HTMLElement | null;

      if (!triggerEl || triggerEl === this.currentTriggerElement) {
        return;
      }

      const popoverId = triggerEl.getAttribute(POPOVER_ID_DATA_ATTR);

      if (!popoverId) {
        return;
      }

      const popoverData = this.popovers()?.[popoverId];
      if (!popoverData) {
        return;
      }

      this.currentTriggerElement = triggerEl;
      this.showPopover(triggerEl, popoverData.content, popoverData.linkUrl);
    }),
  );

  private readonly mouseLeaveTrigger$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mouseout').pipe(
    filter((event) => {
      const target = event.target as HTMLElement;
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      const triggerEl = target.closest(`.${POPOVER_TRIGGER_CLASS}`);

      if (!triggerEl) {
        return false;
      }

      return !relatedTarget || !triggerEl.contains(relatedTarget);
    }),
    tap(() => {
      this.currentTriggerElement = null;
      this.isMouseOverTrigger$$.next(false);
    }),
  );

  onPopoverMouseEnter(): void {
    this.isMouseOverPopover$$.next(true);
  }

  onPopoverMouseLeave(): void {
    this.isMouseOverPopover$$.next(false);
  }

  hidePopover(): void {
    this.isMouseOverPopover$$.next(false);
    this.isMouseOverTrigger$$.next(false);
    this.closePopover();
  }

  private showPopover(target: HTMLElement, blocks: ContentBlock[], linkUrl?: string): void {
    const menuTrigger = this.menuTrigger();
    const wasOpen = menuTrigger.menuOpen;

    this.isMouseOverTrigger$$.next(true);
    this.popoverContent.set(blocks);
    this.popoverLinkUrl.set(linkUrl);
    this.updateAnchorPosition(target);

    // `MatMenuTrigger.openMenu()` is a no-op when already open, and the overlay won't reposition when the
    // trigger element moves. Since our trigger is a fixed-position anchor that we move via bindings,
    // we need to explicitly re-calc the overlay position on quick hover transitions.
    requestAnimationFrame(() => {
      if (!wasOpen) {
        menuTrigger.openMenu();
        return;
      }

      // Only update if still open; a scheduled close might have happened meanwhile.
      if (menuTrigger.menuOpen) {
        menuTrigger.updatePosition();
      }
    });
  }

  private closePopover(): void {
    this.currentTriggerElement = null;
    this.menuTrigger().closeMenu();
  }

  private updateAnchorPosition(target: HTMLElement): void {
    const rect = target.getBoundingClientRect();
    this.anchorPosition.set({
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }

  private closePopoverAfterDelay() {
    return this.shouldBeVisible$.pipe(
      debounceTime(HIDE_DELAY_MS),
      tap((shouldBeVisible) => {
        if (!shouldBeVisible) {
          this.closePopover();
        }
      }),
    );
  }
}
